import React, {FunctionComponent, ChangeEvent, useState, useRef} from 'react';
import cx from 'classnames';

import {MAX_NUMBER_OF_SUGGESTIONS} from '../../../constants/search';

import a11yClick from '../../libs/a11y-click';

import SearchSuggestions, {
  SearchSuggestion
} from '../search-suggestions/search-suggestions';
import IconButton from '../icon-button/icon-button';
import SearchIcon from '../icons/search/search';
import CloseIcon from '../icons/close/close';

import styles from './search.module.css';

const Search: FunctionComponent<Record<string, unknown>> = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedInputValue, setDebouncedValue] = useState<string>('');
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<
    number | null
  >(null);
  const activeSuggestionId =
    activeSuggestionIndex !== null
      ? `search-suggestion-${activeSuggestionIndex}`
      : '';

  // Update the user input value
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setActiveSuggestionIndex(null);

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    // Update debounced value (for search request) after delay
    timeout.current = setTimeout(() => {
      setDebouncedValue(event.target.value);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion: SearchSuggestion) => {
    setInputValue(suggestion.label);
    setActiveSuggestionIndex(null);
  };

  // Focuses / Opens the search input
  const focusSearchInput = () => {
    inputRef.current?.focus();
  };

  // Reset all search states
  const clearSearch = () => {
    setInputValue('');
    setDebouncedValue('');
    setActiveSuggestionIndex(null);
    focusSearchInput();
  };

  // Handles keyboard navigation for search suggestions
  const handleKeyboardNavigation = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    const {key} = event;
    const minIndex = 0;
    const maxIndex = MAX_NUMBER_OF_SUGGESTIONS - 1;

    // Select active suggestion
    if (activeSuggestionIndex !== null && a11yClick(event)) {
      const activeSuggestionElement = document.querySelector(
        `#${activeSuggestionId}`
      ) as HTMLLIElement | null;
      activeSuggestionElement?.click();
      return;
    }

    // Set virtual focus on previous suggestion
    if (key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex(prevState =>
        prevState !== null ? Math.max(minIndex, prevState - 1) : maxIndex
      );
      return;
    }

    // Set virtual focus on next suggestion
    if (key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex(prevState =>
        prevState !== null ? Math.min(maxIndex, prevState + 1) : minIndex
      );
      return;
    }
  };

  // Resets the keyboard selection on input blur
  const handleInputBlur = () => {
    setActiveSuggestionIndex(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputWrapper} onKeyDown={handleKeyboardNavigation}>
        <input
          ref={inputRef}
          className={cx(styles.input, inputValue.length && styles.expanded)}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          autoComplete="off"
          aria-owns="search-suggestions"
          aria-expanded={debouncedInputValue.length >= 3}
          aria-autocomplete="list"
          aria-activedescendant={activeSuggestionId}
          role="combobox"
        />

        {Boolean(inputValue.length) && (
          <button className={styles.clearButton} onClick={clearSearch}>
            <CloseIcon />
          </button>
        )}

        <SearchSuggestions
          className={styles.suggestions}
          inputValue={debouncedInputValue}
          onSelect={handleSelect}
          activeSuggestionIndex={activeSuggestionIndex}
        />

        <IconButton
          className={styles.searchButton}
          variant="secondary"
          onClick={focusSearchInput}>
          <SearchIcon />
        </IconButton>
      </div>
    </div>
  );
};

export default Search;
